import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Search, 
  Download, 
  Play, 
  Image as ImageIcon,
  Video,
  Mic,
  FileText,
  Grid3X3,
  List,
  Copy,
  RefreshCw,
  Sparkles
} from 'lucide-react';
import { useGenovaAI } from '@/hooks/useGenovaAI';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { WatermarkOverlay } from '@/components/WatermarkOverlay';
import { supabase } from '@/integrations/supabase/client';
import { applyWatermarkToImage } from '@/utils/watermark';

interface AssetItem {
  id: string;
  type: 'image' | 'video' | 'voice' | 'script';
  title: string;
  url: string;
  prompt?: string;
  thumbnail_url?: string;
  tags?: string[];
  resolution?: string;
  duration?: number;
  is_favorite?: boolean;
  watermarked?: boolean;
  created_at: string;
  metadata?: any;
}

const AssetLibrary = () => {
  const { user } = useAuth();
  const { getUserContent } = useGenovaAI();
  const [assets, setAssets] = useState<AssetItem[]>([]);
  const [filteredAssets, setFilteredAssets] = useState<AssetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Real-time subscription for new content
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`asset-library-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'generated_content',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('New content added:', payload);
          const newAsset = mapToAssetItem(payload.new);
          setAssets(prev => [newAsset, ...prev]);
          toast.success('New content added to library!', {
            icon: <Sparkles className="w-4 h-4" />
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  useEffect(() => {
    loadAssets();
  }, [user]);

  useEffect(() => {
    filterAssets();
  }, [assets, searchQuery, selectedType, sortBy]);

  const mapToAssetItem = (item: any): AssetItem => ({
    id: item.id,
    type: item.type as 'image' | 'video' | 'voice' | 'script',
    title: item.title || item.prompt?.substring(0, 50) || 'Untitled',
    url: item.url,
    prompt: item.prompt,
    thumbnail_url: item.thumbnail_url,
    tags: item.tags || [],
    resolution: item.resolution,
    duration: item.duration,
    is_favorite: item.is_favorite || false,
    watermarked: item.watermarked !== false,
    created_at: item.created_at,
    metadata: item.metadata
  });

  const loadAssets = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const content = await getUserContent();
      const mappedAssets: AssetItem[] = content.map(mapToAssetItem);
      setAssets(mappedAssets);
    } catch (error) {
      console.error('Error loading assets:', error);
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const filterAssets = () => {
    let filtered = [...assets];

    if (searchQuery) {
      filtered = filtered.filter(asset => 
        asset.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset.prompt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (selectedType !== 'all') {
      filtered = filtered.filter(asset => asset.type === selectedType);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    setFilteredAssets(filtered);
  };

  const handleDownload = async (asset: AssetItem) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('plan')
        .eq('user_id', user?.id)
        .single();
      
      const plan = profile?.plan || 'free';
      const isFreeUser = plan === 'free';
      
      let downloadUrl = asset.url;
      let filename = `${asset.title.replace(/[^a-z0-9]/gi, '_')}_${asset.id}`;
      
      if (asset.type === 'script') {
        // Decode base64 script content
        if (asset.url.startsWith('data:text/plain;base64,')) {
          const base64Content = asset.url.replace('data:text/plain;base64,', '');
          const textContent = atob(base64Content);
          const blob = new Blob([textContent], { type: 'text/plain' });
          downloadUrl = URL.createObjectURL(blob);
          filename += '.txt';
        }
      } else if (asset.type === 'voice') {
        filename += '.mp3';
      } else if (asset.type === 'image') {
        if (isFreeUser && asset.watermarked) {
          toast.info('Applying watermark...');
          try {
            downloadUrl = await applyWatermarkToImage(asset.url);
          } catch (error) {
            console.error('Error applying watermark:', error);
          }
        }
        filename += '.png';
      } else {
        filename += '.mp4';
      }
      
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      if (downloadUrl.startsWith('blob:')) {
        setTimeout(() => URL.revokeObjectURL(downloadUrl), 100);
      }
      
      toast.success('Download completed!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Download failed. Please try again.');
    }
  };

  const handleCopyScript = async (asset: AssetItem) => {
    if (asset.type !== 'script') return;
    
    try {
      let textContent = '';
      if (asset.url.startsWith('data:text/plain;base64,')) {
        const base64Content = asset.url.replace('data:text/plain;base64,', '');
        textContent = atob(base64Content);
      }
      
      await navigator.clipboard.writeText(textContent);
      toast.success('Script copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy script');
    }
  };

  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'image': return <ImageIcon className="w-4 h-4" />;
      case 'video': return <Video className="w-4 h-4" />;
      case 'voice': return <Mic className="w-4 h-4" />;
      case 'script': return <FileText className="w-4 h-4" />;
      default: return <ImageIcon className="w-4 h-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'image': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'video': return 'bg-purple-500/10 text-purple-500 border-purple-500/20';
      case 'voice': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'script': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const AssetCard = ({ asset }: { asset: AssetItem }) => (
    <Card className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-2 border-transparent hover:border-primary/20 rounded-2xl">
      <div className="relative aspect-video bg-gradient-to-br from-muted to-muted/50">
        {asset.type === 'image' ? (
          <WatermarkOverlay showWatermark={!!asset.watermarked}>
            <img 
              src={asset.url} 
              alt={asset.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </WatermarkOverlay>
        ) : asset.type === 'video' ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-900/20 to-purple-500/10">
            <Play className="w-12 h-12 text-purple-500" />
          </div>
        ) : asset.type === 'voice' ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-900/20 to-green-500/10">
            <Mic className="w-12 h-12 text-green-500" />
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-orange-900/20 to-orange-500/10 p-4">
            <FileText className="w-10 h-10 text-orange-500 mb-2" />
            <p className="text-xs text-center text-muted-foreground line-clamp-3">
              {asset.prompt || asset.title}
            </p>
          </div>
        )}
        
        {/* Type Badge */}
        <div className="absolute top-3 left-3">
          <Badge className={`${getTypeColor(asset.type)} border text-xs font-medium`}>
            {getAssetIcon(asset.type)}
            <span className="ml-1 capitalize">{asset.type}</span>
          </Badge>
        </div>
        
        {/* Action Buttons */}
        <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            size="icon"
            variant="secondary"
            className="w-8 h-8 rounded-lg shadow-lg"
            onClick={() => handleDownload(asset)}
          >
            <Download className="w-4 h-4" />
          </Button>
          {asset.type === 'script' && (
            <Button
              size="icon"
              variant="secondary"
              className="w-8 h-8 rounded-lg shadow-lg"
              onClick={() => handleCopyScript(asset)}
            >
              <Copy className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Audio Player for Voice */}
        {asset.type === 'voice' && (
          <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
            <audio src={asset.url} controls className="w-full h-8" />
          </div>
        )}
      </div>
      
      <CardContent className="p-4">
        <h3 className="font-semibold text-sm mb-2 line-clamp-2">{asset.title}</h3>
        
        {asset.tags && asset.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {asset.tags.slice(0, 3).map((tag, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
          </div>
        )}
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{new Date(asset.created_at).toLocaleDateString()}</span>
          {asset.duration && <span>{asset.duration}s</span>}
          {asset.resolution && <span>{asset.resolution}</span>}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-primary to-pink-500 bg-clip-text text-transparent">
            Asset Library
          </h2>
          <p className="text-muted-foreground">Browse and manage your generated content</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={loadAssets}
            disabled={loading}
            className="rounded-xl"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('grid')}
            className="rounded-xl"
          >
            <Grid3X3 className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="icon"
            onClick={() => setViewMode('list')}
            className="rounded-xl"
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search assets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 rounded-xl border-2"
          />
        </div>
        
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="w-full md:w-40 h-12 rounded-xl border-2">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="image">Images</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
            <SelectItem value="voice">Voice</SelectItem>
            <SelectItem value="script">Scripts</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full md:w-40 h-12 rounded-xl border-2">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="oldest">Oldest</SelectItem>
            <SelectItem value="title">Title</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Assets Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <Card key={i} className="animate-pulse rounded-2xl overflow-hidden">
              <div className="aspect-video bg-muted" />
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded mb-2" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-10 h-10 text-muted-foreground" />
          </div>
          <h3 className="font-semibold text-lg mb-2">No assets found</h3>
          <p className="text-muted-foreground mb-4">
            {searchQuery ? 'Try adjusting your search or filters' : 'Start creating content to see it here'}
          </p>
          {!searchQuery && (
            <Button onClick={() => window.location.href = '/dashboard'}>
              <Sparkles className="w-4 h-4 mr-2" />
              Create Content
            </Button>
          )}
        </div>
      ) : (
        <div className={`grid gap-6 ${
          viewMode === 'grid' 
            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
            : 'grid-cols-1'
        }`}>
          {filteredAssets.map((asset) => (
            <AssetCard key={asset.id} asset={asset} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AssetLibrary;
